<template>
  <div class="card edit-card">
    <div class="card-body">
      <div v-if="isArchived" class="archived-banner">
        <i class="fas fa-archive mr-2"></i>
        アーカイブ済みスタッフを編集しています。所属店舗は変更できません。復元すると退職日はクリアされます。
      </div>
      <div class="edit-grid">
        <div class="edit-section">
          <h6 class="section-title"><i class="fas fa-user mr-2"></i>基本情報</h6>
          <div class="field-group">
            <label>名前 <span class="required">*</span></label>
            <input type="text" class="form-control" v-model="formData.name">
          </div>
          <div class="field-group">
            <label>メールアドレス <span class="required">*</span></label>
            <input type="text" class="form-control" v-model="formData.email">
          </div>
          <div class="field-group">
            <label>パスワード</label>
            <input type="text" class="form-control" v-model="formData.password" placeholder="変更する場合のみ入力">
          </div>
          <div class="field-group">
            <label>所属店舗</label>
            <div class="shop-checkbox-list">
              <label class="shop-checkbox-item" v-for="shop in shops" :key="shop.id">
                <input type="checkbox" :value="shop.id" v-model="selectedShopIds" :disabled="isArchived">
                <span class="shop-checkbox-name">{{ shop.shop_name }}</span>
              </label>
            </div>
          </div>
          <div class="field-row">
            <div class="field-group">
              <label>年齢</label>
              <input type="number" class="form-control" v-model="formData.age">
            </div>
            <div class="field-group">
              <label>性別</label>
              <select class="form-select" v-model="formData.gender">
                <option value="">選択してください</option>
                <option value="男">男</option>
                <option value="女">女</option>
                <option value="特定しない">特定しない</option>
              </select>
            </div>
          </div>
          <div class="field-row">
            <div class="field-group">
              <label>電話番号</label>
              <input type="text" class="form-control" v-model="formData.phone_number" @keypress="validateNumber">
            </div>
            <div class="field-group">
              <label>郵便番号</label>
              <input type="text" class="form-control" v-model="formData.postal_code" @keypress="validateNumber">
            </div>
          </div>
          <div class="field-group">
            <label>住所</label>
            <textarea class="form-control" rows="3" v-model="formData.address"></textarea>
          </div>
          <div class="field-group">
            <label>入社日</label>
            <input type="date" v-model="formData.date_of_joining" class="form-control">
          </div>
          <div class="field-group">
            <label>退職日</label>
            <input type="date" v-model="formData.date_of_leaving" class="form-control">
          </div>
        </div>

        <div class="edit-section">
          <h6 class="section-title"><i class="fas fa-briefcase mr-2"></i>勤務情報</h6>
          <div class="field-group">
            <label>勤務時間 <span class="required">*</span></label>
            <div class="time-range">
              <input type="time" v-model="formData.regular_working_hours_start" class="form-control" />
              <span class="range-sep">〜</span>
              <input type="time" v-model="formData.regular_working_hours_stop" class="form-control" />
            </div>
          </div>
          <div class="field-row">
            <div class="field-group">
              <label>休憩 <span class="required">*</span></label>
              <div class="input-with-unit">
                <input type="number" v-model="formData.break_time" class="form-control" />
                <span class="unit">分</span>
              </div>
            </div>
            <div class="field-group">
              <label>時給</label>
              <div class="input-with-unit">
                <span class="unit-prefix">¥</span>
                <input type="number" class="form-control" v-model="formData.hourly_wage">
              </div>
            </div>
          </div>
          <div class="field-group">
            <label>日当交通費</label>
            <div class="input-with-unit">
              <span class="unit-prefix">¥</span>
              <input type="number" class="form-control" v-model="formData.daily_transportation_allowance">
              <span class="unit">/日</span>
            </div>
          </div>
          <div class="field-group">
            <label style="display:flex;align-items:center;gap:8px;">
              <input type="checkbox" v-model="formData.use_paid_leave" :true-value="1" :false-value="0" style="width:16px;height:16px;cursor:pointer;">
              有給を利用する
            </label>
            <div v-if="formData.use_paid_leave != 1" style="margin-top:4px;font-size:11px;color:#a0aec0;">
              ※ OFFの場合、アプリ側で有給申請の機能が表示されません
            </div>
          </div>
          <div class="field-group" v-if="formData.use_paid_leave == 1">
            <label style="display:flex;align-items:center;gap:8px;">
              <input type="checkbox" v-model="formData.paid_leave" :true-value="1" :false-value="0" style="width:16px;height:16px;cursor:pointer;">
              有給残日数を管理する
              <button type="button" class="btn btn-sm btn-outline-info ml-2" @click="openLeaveHistory" style="font-size:11px;padding:2px 8px;">
                <i class="fas fa-history mr-1"></i>取得履歴
              </button>
            </label>
            <div class="input-with-unit">
              <input type="number" step="0.5" class="form-control" v-model="formData.remaining_paid_leave_days"
                     :disabled="formData.paid_leave != 1"
                     :style="{ backgroundColor: formData.paid_leave != 1 ? '#f0f0f0' : '#fff' }">
              <span class="unit">日</span>
            </div>
            <div v-if="leaveHistory.used_days !== null" style="margin-top:6px;font-size:12px;color:#4a5568;">
              使用日数: <strong style="color:#d97706;">{{ leaveHistory.used_days }}日</strong> (承認済み)
            </div>
            <div v-if="formData.paid_leave != 1" style="margin-top:4px;font-size:11px;color:#a0aec0;">
              ※ 残日数管理OFFのため、有給申請しても残日数は減りません
            </div>
          </div>
          <!-- Holiday type -->
          <div class="field-group">
            <label>休日タイプ</label>
            <div class="holiday-type-selector">
              <button type="button" :class="['holiday-btn', { active: formData.holiday_type === 1 }]" @click="formData.holiday_type = 1">
                <i class="fas fa-calendar-check mr-1"></i>土日祝
              </button>
              <button type="button" :class="['holiday-btn', { active: formData.holiday_type === 2 }]" @click="formData.holiday_type = 2">
                <i class="fas fa-sync-alt mr-1"></i>シフト制
              </button>
            </div>
          </div>
          <div class="field-group" v-if="formData.holiday_type === 2">
            <label>月あたりの休日数</label>
            <div class="input-with-unit">
              <input type="number" class="form-control" v-model="formData.monthly_days_off" min="0" max="31" style="width:80px;">
              <span class="unit">日 / 月</span>
            </div>
          </div>

          <!-- Shift detail fields -->
          <div class="field-row">
            <div class="field-group">
              <label>週上限時間</label>
              <div class="input-with-unit">
                <input type="number" class="form-control" v-model="formData.weekly_cap_hours" step="0.5" style="width:80px;">
                <span class="unit">時間</span>
              </div>
            </div>
            <div class="field-group">
              <label>週稼働日数</label>
              <div class="input-with-unit">
                <input type="number" class="form-control" v-model="formData.weekly_target_days_min" min="0" max="7" style="width:60px;">
                <span class="unit">〜</span>
                <input type="number" class="form-control" v-model="formData.weekly_target_days_max" min="0" max="7" style="width:60px;">
                <span class="unit">日</span>
              </div>
            </div>
          </div>

          <div class="toggle-group">
            <div class="toggle-item">
              <label class="toggle-label" for="can_close">クローズ業務</label>
              <label class="switch">
                <input type="checkbox" id="can_close" v-model="formData.can_close">
                <span class="slider"></span>
              </label>
            </div>
            <div class="toggle-item">
              <label class="toggle-label" for="time_adjustment">タイムカード調整を行う</label>
              <label class="switch">
                <input type="checkbox" id="time_adjustment" v-model="formData.time_adjustment">
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Work Patterns -->
      <div class="patterns-edit-section">
        <div class="patterns-header">
          <h6 class="section-title mb-0"><i class="fas fa-clock mr-2"></i>勤務パターン</h6>
          <button class="btn btn-sm btn-success" @click="addPattern"><i class="fas fa-plus mr-1"></i>追加</button>
        </div>

        <div v-if="workPatterns.length === 0" class="text-muted text-center py-3">
          勤務パターンが登録されていません
        </div>

        <div v-for="(p, idx) in workPatterns" :key="idx" class="pattern-edit-card">
          <div class="pattern-edit-top">
            <div class="field-group mb-0" style="flex:1;">
              <label>パターン名</label>
              <input type="text" class="form-control form-control-sm" v-model="p.pattern_name" placeholder="例: 通常, 早番, 遅番">
            </div>
            <button class="btn btn-sm btn-outline-danger pattern-delete-btn" @click="removePattern(idx)" title="削除">
              <i class="fas fa-trash"></i>
            </button>
          </div>
          <div class="field-row mt-2">
            <div class="field-group mb-0">
              <label>開始</label>
              <input type="time" class="form-control form-control-sm" v-model="p.start_time">
            </div>
            <div class="field-group mb-0">
              <label>終了</label>
              <input type="time" class="form-control form-control-sm" v-model="p.end_time">
            </div>
            <div class="field-group mb-0">
              <label>休憩</label>
              <div class="input-with-unit">
                <input type="number" class="form-control form-control-sm" v-model="p.break_time" style="width:60px;">
                <span class="unit">分</span>
              </div>
            </div>
          </div>
          <div class="pattern-days-edit mt-2">
            <label class="day-check" v-for="d in dayDefs" :key="d.key">
              <input type="checkbox" v-model="p[d.key]">
              <span :class="['day-label', d.cls]">{{ d.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Day Constraints -->
      <div class="patterns-edit-section">
        <div class="patterns-header">
          <h6 class="section-title mb-0"><i class="fas fa-calendar-week mr-2"></i>曜日別勤務制約</h6>
        </div>
        <p class="text-muted mb-2" style="font-size:12px;">曜日ごとの勤務可能時間帯を設定します。空欄は制限なし。</p>
        <div class="dc-edit-grid">
          <div v-for="c in dayConstraints" :key="c.day_of_week" class="dc-edit-card">
            <div class="dc-edit-header">
              <span class="dc-edit-day">{{ dayConstraintNames[c.day_of_week] }}</span>
              <label class="dc-edit-toggle">
                <input type="checkbox" v-model="c.is_available">
                <span class="dc-toggle-text">{{ c.is_available ? '勤務可' : '不可' }}</span>
              </label>
            </div>
            <div v-if="c.is_available" class="dc-edit-times">
              <div class="dc-edit-field">
                <label>開始</label>
                <input type="time" class="form-control form-control-sm" v-model="c.available_from">
              </div>
              <div class="dc-edit-field">
                <label>終了</label>
                <input type="time" class="form-control form-control-sm" v-model="c.available_until">
              </div>
            </div>
            <div v-if="c.is_available" class="dc-edit-field mt-1">
              <input type="text" class="form-control form-control-sm" v-model="c.note" placeholder="メモ">
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer edit-footer">
      <button class="btn btn-outline-secondary" @click="this.$router.push({name:'CompanyStaffDetailsPage', params: {staff_id: this.$route.params.staff_id}});">キャンセル</button>
      <button class="btn btn-primary" @click="dialog"><i class="fas fa-save mr-1"></i>保存</button>
    </div>
  </div>

  <div v-if="showConfirmModal" class="modal-overlay" @click.self="showConfirmModal = false">
    <div class="confirm-modal">
      <div class="confirm-modal-body text-center py-4">
        <p class="mb-1">従業員情報を更新します。</p>
        <p class="text-muted mb-0">よろしいですか？</p>
      </div>
      <div class="confirm-modal-footer">
        <button type="button" class="btn btn-outline-secondary" @click="showConfirmModal = false">キャンセル</button>
        <button type="button" class="btn btn-primary" @click="register">保存する</button>
      </div>
    </div>
  </div>

  <!-- 有給取得履歴モーダル -->
  <div v-if="showLeaveHistoryModal" class="leave-history-overlay" @click.self="showLeaveHistoryModal = false">
    <div class="leave-history-box">
      <div class="leave-history-header">
        <h6 class="leave-history-title"><i class="fas fa-history mr-2"></i>{{ formData.name || 'スタッフ' }} - 有給取得履歴</h6>
        <button type="button" class="leave-history-close" @click="showLeaveHistoryModal = false">&times;</button>
      </div>
      <div class="leave-history-body">
        <div class="leave-history-summary">
          <div><span class="lh-label">有給残日数</span><span class="lh-value">{{ formData.remaining_paid_leave_days || 0 }}日</span></div>
          <div><span class="lh-label">使用日数(承認済み)</span><span class="lh-value lh-used">{{ leaveHistory.used_days || 0 }}日</span></div>
          <div><span class="lh-label">申請数</span><span class="lh-value">{{ leaveHistory.leaves.length }}件</span></div>
        </div>
        <div v-if="leaveHistory.leaves.length === 0" class="text-center text-muted py-3">取得履歴がありません</div>
        <table v-else class="table table-sm leave-history-table">
          <thead>
            <tr>
              <th>日付</th>
              <th>種別</th>
              <th>消費</th>
              <th>状態</th>
              <th>理由</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in leaveHistory.leaves" :key="l.id">
              <td>{{ l.date }}</td>
              <td>{{ l.leave_type_text }}</td>
              <td>{{ l.consumed_days }}日</td>
              <td><span :class="leaveStatusClass(l.approval_status)">{{ l.approval_status_text }}</span></td>
              <td class="lh-reason">{{ l.reason || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="leave-history-footer">
        <button v-if="primaryShopId" type="button" class="btn btn-success btn-sm mr-2" @click="openLeaveCreate">
          <i class="fas fa-plus mr-1"></i>新規作成
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="showLeaveHistoryModal = false">閉じる</button>
      </div>
    </div>
  </div>

  <!-- 有給 新規作成モーダル -->
  <div v-if="showLeaveCreateModal" class="leave-history-overlay" @click.self="showLeaveCreateModal = false">
    <div class="leave-history-box" style="width:460px;">
      <div class="leave-history-header">
        <h6 class="leave-history-title"><i class="fas fa-plus mr-2"></i>{{ formData.name }} - 有給作成</h6>
        <button type="button" class="leave-history-close" @click="showLeaveCreateModal = false">&times;</button>
      </div>
      <div class="leave-history-body">
        <div style="margin-bottom:12px;font-size:13px;color:#4a5568;">
          <strong>対象スタッフ:</strong> {{ formData.name }}
        </div>
        <div style="margin-bottom:12px;">
          <label style="font-size:13px;font-weight:600;color:#4a5568;">種別 <span style="color:#e53e3e;">*</span></label>
          <div style="display:flex;gap:16px;padding-top:4px;">
            <label><input type="radio" :value="1" v-model="leaveCreateForm.leave_type"> 全日</label>
            <label><input type="radio" :value="2" v-model="leaveCreateForm.leave_type"> 半日</label>
          </div>
        </div>
        <div style="display:flex;gap:12px;margin-bottom:12px;">
          <div style="flex:1;">
            <label style="font-size:13px;font-weight:600;color:#4a5568;">開始日 <span style="color:#e53e3e;">*</span></label>
            <input type="date" v-model="leaveCreateForm.start_date" class="form-control" @change="onLeaveStartChange">
          </div>
          <div style="flex:1;" v-if="leaveCreateForm.leave_type !== 2">
            <label style="font-size:13px;font-weight:600;color:#4a5568;">終了日 <span style="color:#e53e3e;">*</span></label>
            <input type="date" v-model="leaveCreateForm.end_date" :min="leaveCreateForm.start_date" class="form-control">
          </div>
        </div>
        <div style="margin-bottom:8px;">
          <label style="font-size:13px;font-weight:600;color:#4a5568;">理由</label>
          <textarea v-model="leaveCreateForm.reason" class="form-control" rows="2" placeholder="任意"></textarea>
        </div>
        <p style="font-size:12px;color:#718096;margin:0;">※ 管理者作成のため<strong>承認済み</strong>として登録されます</p>
      </div>
      <div class="leave-history-footer">
        <button class="btn btn-outline-secondary btn-sm mr-2" @click="showLeaveCreateModal = false">キャンセル</button>
        <button class="btn btn-success btn-sm" @click="submitLeaveCreate">
          <i class="fas fa-check mr-1"></i>作成
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      formData: {
        name: null,
        email: null,
        password: null,
        regular_working_hours_start: null,
        regular_working_hours_stop: null,
        break_time: null,
        hourly_wage: null,
        remaining_paid_leave_days: null,
        age: null,
        gender: null,
        phone_number: null,
        postal_code: null,
        address: null,
        date_of_joining: null,
        date_of_leaving: null,
        daily_transportation_allowance: null,
        manager: false,
        time_adjustment: false,
        paid_leave: 0,
        use_paid_leave: 0,
        holiday_type: 1,
        monthly_days_off: 8,
        weekly_cap_hours: null,
        weekly_target_days_min: null,
        weekly_target_days_max: null,
        can_close: true,
      },
      staff_id: this.$route.params.staff_id,
      shops: [],
      selectedShopIds: [],
      workPatterns: [],
      dayConstraints: [],
      dayConstraintNames: ['日', '月', '火', '水', '木', '金', '土'],
      dayDefs: [
        { key: 'monday', label: '月', cls: '' },
        { key: 'tuesday', label: '火', cls: '' },
        { key: 'wednesday', label: '水', cls: '' },
        { key: 'thursday', label: '木', cls: '' },
        { key: 'friday', label: '金', cls: '' },
        { key: 'saturday', label: '土', cls: 'sat' },
        { key: 'sunday', label: '日', cls: 'sun' },
      ],
      showConfirmModal: false,
      // 有給取得履歴
      showLeaveHistoryModal: false,
      leaveHistory: { leaves: [], used_days: null },
      // 有給新規作成モーダル
      showLeaveCreateModal: false,
      leaveCreateForm: {
        leave_type: 1,
        start_date: '',
        end_date: '',
        reason: '',
      },
    }
  },
  computed: {
    isArchived() {
      return !!this.formData.deleted_at;
    },
    primaryShopId() {
      return this.selectedShopIds && this.selectedShopIds.length > 0 ? this.selectedShopIds[0] : null;
    },
  },
  methods: {
    loadLeaveHistory() {
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/staff/' + this.staff_id + '/paid-leaves')
        .then(res => {
          if (res.data?.success) {
            this.leaveHistory = res.data.data;
          }
        })
        .catch(() => { /* silent */ });
    },
    openLeaveHistory() {
      this.loadLeaveHistory();
      this.showLeaveHistoryModal = true;
    },
    _todayStr() {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    openLeaveCreate() {
      const t = this._todayStr();
      this.leaveCreateForm = {
        leave_type: 1,
        start_date: t,
        end_date: t,
        reason: '',
      };
      this.showLeaveCreateModal = true;
    },
    onLeaveStartChange() {
      if (this.leaveCreateForm.end_date < this.leaveCreateForm.start_date) {
        this.leaveCreateForm.end_date = this.leaveCreateForm.start_date;
      }
    },
    async submitLeaveCreate() {
      const f = this.leaveCreateForm;
      if (!f.start_date) {
        this.$toast.error('開始日を入力してください');
        return;
      }
      const shop_id = this.primaryShopId;
      if (!shop_id) {
        this.$toast.error('所属店舗が設定されていないため作成できません');
        return;
      }
      const payload = {
        staff_id: parseInt(this.staff_id),
        leave_type: f.leave_type,
        start_date: f.start_date,
        end_date: f.leave_type === 2 ? f.start_date : f.end_date,
        reason: f.reason || null,
      };
      try {
        const res = await this.$http.post(
          process.env.VUE_APP_API_PATH + '/shop/paidLeave/admin-create/' + shop_id,
          payload
        );
        const created = res.data?.data?.created || 0;
        this.$toast.success(created + '件の有給を作成しました');
        this.showLeaveCreateModal = false;
        this.loadLeaveHistory();
      } catch (e) {
        const msg = e.response?.data?.message || '作成に失敗しました';
        this.$toast.error(msg);
      }
    },
    leaveStatusClass(status) {
      if (status === 2) return 'lh-status lh-status-approved';
      if (status === 1) return 'lh-status lh-status-pending';
      return 'lh-status lh-status-rejected';
    },
    addPattern() {
      this.workPatterns.push({
        id: null,
        pattern_name: '',
        start_time: this.formData.regular_working_hours_start || '09:00',
        end_time: this.formData.regular_working_hours_stop || '18:00',
        break_time: this.formData.break_time || 60,
        monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
        saturday: false, sunday: false,
      });
    },
    removePattern(idx) {
      this.workPatterns.splice(idx, 1);
    },
    saveDayConstraints() {
      const toSave = this.dayConstraints.filter(c =>
        !c.is_available || c.available_from || c.available_until || c.note
      );
      if (toSave.length === 0) return Promise.resolve();
      return this.$http.post(
        process.env.VUE_APP_API_PATH + '/company/staff/' + this.staff_id + '/day-constraints/bulk',
        { constraints: toSave }
      );
    },
    saveWorkPatterns() {
      return this.$http.post(
        process.env.VUE_APP_API_PATH + '/company/staff/' + this.staff_id + '/work-patterns/bulk',
        { patterns: this.workPatterns }
      );
    },
    validateNumber(event) {
      const key = event.key;
      if (!/[0-9]/.test(key)) {
          event.preventDefault();
      }
    },
    dialog() {
      this.showConfirmModal = true;
    },
    dialogClose() {
      this.showConfirmModal = false;
    },
    getStaff () {
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/staff/'+this.staff_id, { params: { with_trashed: 1 } })
      .then(response => {
        if (response.data.success) {
          this.formData = response.data.data;
          this.formData.email = response.data.data.user.email;

          // time_adjustment/can_close は :true-value 指定なしのチェックボックスなのでboolean
          this.formData.time_adjustment = response.data.data.time_adjustment > 0;
          // paid_leave/use_paid_leave は :true-value="1" :false-value="0" 指定のため整数で揃える
          this.formData.paid_leave = response.data.data.paid_leave > 0 ? 1 : 0;
          this.formData.use_paid_leave = response.data.data.use_paid_leave > 0 ? 1 : 0;

          this.formData.can_close = response.data.data.can_close !== false && response.data.data.can_close !== 0;
          this.formData.weekly_cap_hours = response.data.data.weekly_cap_hours;
          this.formData.weekly_target_days_min = response.data.data.weekly_target_days_min;
          this.formData.weekly_target_days_max = response.data.data.weekly_target_days_max;

          // Populate selected shop IDs from staff's shop affiliations
          if (response.data.data.shop && Array.isArray(response.data.data.shop)) {
            this.selectedShopIds = response.data.data.shop.map(s => s.id);
          }

          // Day constraints
          const existingConstraints = response.data.data.day_constraints || [];
          this.dayConstraints = [];
          for (let dow = 0; dow < 7; dow++) {
            const existing = existingConstraints.find(c => c.day_of_week === dow);
            if (existing) {
              this.dayConstraints.push({
                day_of_week: dow,
                available_from: existing.available_from ? existing.available_from.slice(0, 5) : null,
                available_until: existing.available_until ? existing.available_until.slice(0, 5) : null,
                is_available: existing.is_available,
                note: existing.note,
              });
            } else {
              this.dayConstraints.push({
                day_of_week: dow,
                available_from: null,
                available_until: null,
                is_available: true,
                note: null,
              });
            }
          }

          if (response.data.data.work_patterns && response.data.data.work_patterns.length > 0) {
            this.workPatterns = response.data.data.work_patterns.map(p => ({
              ...p,
              start_time: p.start_time ? p.start_time.slice(0, 5) : '',
              end_time: p.end_time ? p.end_time.slice(0, 5) : '',
            }));
          } else {
            this.workPatterns = [{
              id: null,
              pattern_name: '通常',
              start_time: this.formData.regular_working_hours_start || '09:00',
              end_time: this.formData.regular_working_hours_stop || '18:00',
              break_time: this.formData.break_time || 60,
              monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
              saturday: false, sunday: false,
            }];
          }
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data.message);
        }
      });
    },
    register () {
      const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[#?!@$%^&:*-])[A-Za-z\d#?!@$%^&:*-]{8,16}$/;
      if (this.formData.password) {
        if (!passwordRegex.test(this.formData.password)) {
          this.$toast.error('英⼤⽂字、⼩⽂字、記号（#?!@$%^&*-）、数字を各１以上含む８⽂字以上で作成してください');
          return false;
        }
      }
      this.formData.shop = this.selectedShopIds;
      this.formData.company_id = this.$store.getters.company;
      this.$http.put(process.env.VUE_APP_API_PATH + '/company/staff/'+this.$route.params.staff_id, this.formData, { params: { with_trashed: 1 } })
      .then(response => {
        if (response.data.success) {
          return Promise.all([
            this.saveWorkPatterns(),
            this.saveDayConstraints(),
          ]).then(() => {
            this.$toast.success(response.data.message);
            setTimeout(() => {
              this.$router.push({name:'CompanyStaffDetailsPage', params: {staff_id: this.$route.params.staff_id}});
            }, 1000);
          });
        }
      })
      .catch(error => {
        if (error.response) {
          const response = error.response.data;
          if (response?.errors?.email) {
            this.$toast.error(response.errors.email[0]);
          } else if (response?.errors?.password) {
            this.$toast.error(response.errors.password[0]);
          } else {
            this.$toast.error(response?.message || 'エラーが発生しました');
          }
        }
      });

      this.dialogClose();
    },
    getShopList () {
      const company_id = this.$store.getters.company;
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/shopList/'+company_id)
      .then(response => {
        if (response.data.success) {
          this.shops = response.data.data;
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data.message);
        }
      });
    },
  },
  mounted() {
    this.getStaff();
    this.getShopList();
    this.loadLeaveHistory();
  },
};
</script>

<style scoped>
.edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

@media (max-width: 768px) {
  .edit-grid {
    grid-template-columns: 1fr;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #049C4C;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: 10px;
  margin-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.field-group {
  margin-bottom: 16px;
}

.field-group label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 4px;
  display: block;
}

.required {
  color: #e53e3e;
  font-weight: 700;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field-row select.form-control,
.field-row select.form-select {
  min-width: 0;
  width: 100%;
  text-overflow: ellipsis;
  padding-right: 32px;
  line-height: 1.5;
}

.time-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-sep {
  color: #718096;
  font-weight: 600;
}

.input-with-unit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.unit {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
  white-space: nowrap;
}

.unit-prefix {
  font-size: 16px;
  color: #718096;
  font-weight: 600;
}

/* Shop checkbox list */
.shop-checkbox-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 180px;
  overflow-y: auto;
  padding: 8px;
}

.shop-checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.shop-checkbox-item:hover {
  background: #f0fff4;
}

.shop-checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #049C4C;
  cursor: pointer;
  flex-shrink: 0;
}

.shop-checkbox-name {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
}

/* Toggle switch */
.toggle-group {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.2s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  transition: 0.2s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #049C4C;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* Holiday type selector */
.holiday-type-selector {
  display: flex;
  gap: 8px;
}

.holiday-btn {
  flex: 1;
  padding: 10px 16px;
  border: 1.5px solid #d2d6dc;
  border-radius: 8px;
  background: #fff;
  color: #4a5568;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.holiday-btn:hover {
  border-color: #049C4C;
  color: #049C4C;
}

.holiday-btn.active {
  background: #049C4C;
  border-color: #049C4C;
  color: #fff;
}

/* Work Patterns edit */
.patterns-edit-section {
  margin-top: 32px;
}

.patterns-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.pattern-edit-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #f7fafc;
}

.pattern-edit-top {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.pattern-delete-btn {
  height: 31px;
  flex-shrink: 0;
}

.pattern-days-edit {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.day-check {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  margin: 0;
}

.day-check input[type="checkbox"] {
  display: none;
}

.day-label {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #e2e8f0;
  color: #a0aec0;
  transition: all 0.15s ease;
  cursor: pointer;
}

.day-check input:checked + .day-label {
  background: #049C4C;
  color: #fff;
}

.day-check input:checked + .day-label.sat {
  background: #3182ce;
}

.day-check input:checked + .day-label.sun {
  background: #e53e3e;
}

/* Day Constraints edit */
.dc-edit-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

@media (max-width: 992px) {
  .dc-edit-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 576px) {
  .dc-edit-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.dc-edit-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  background: #f7fafc;
}

.dc-edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.dc-edit-day {
  font-size: 14px;
  font-weight: 700;
  color: #1a202c;
}

.dc-edit-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  margin: 0;
}

.dc-edit-toggle input[type="checkbox"] {
  width: 14px;
  height: 14px;
}

.dc-toggle-text {
  font-size: 10px;
  font-weight: 600;
  color: #718096;
}

.dc-edit-times {
  display: flex;
  gap: 4px;
}

.dc-edit-field {
  flex: 1;
}

.dc-edit-field label {
  font-size: 10px;
  font-weight: 600;
  color: #718096;
  margin-bottom: 1px;
  display: block;
}

.dc-edit-field .form-control-sm {
  font-size: 12px;
  padding: 3px 6px;
}

.edit-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4); z-index: 1050;
  display: flex; align-items: center; justify-content: center;
}
.confirm-modal {
  background: #fff; border-radius: 12px; width: 340px; max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
}
.confirm-modal-footer {
  display: flex; justify-content: center; gap: 12px;
  padding: 12px 20px 20px;
}

/* 有給取得履歴モーダル */
.leave-history-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.45); z-index: 1060;
  display: flex; align-items: center; justify-content: center;
}
.leave-history-box {
  background: #fff; border-radius: 12px; width: 720px; max-width: 95vw; max-height: 85vh;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden;
}
.leave-history-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
}
.leave-history-title { font-weight: 700; font-size: 16px; margin: 0; color: #1a202c; }
.leave-history-close { border: none; background: none; font-size: 24px; color: #718096; cursor: pointer; padding: 0 4px; line-height: 1; }
.leave-history-close:hover { color: #e53e3e; }
.leave-history-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.leave-history-summary {
  display: flex; gap: 16px; margin-bottom: 14px; padding: 12px 14px;
  background: #f7fafc; border-radius: 8px; border: 1px solid #e2e8f0;
}
.leave-history-summary > div { display: flex; flex-direction: column; gap: 2px; }
.lh-label { font-size: 11px; color: #718096; font-weight: 600; }
.lh-value { font-size: 18px; font-weight: 700; color: #1a202c; }
.lh-used { color: #d97706; }
.leave-history-table { font-size: 13px; }
.leave-history-table th { background: #f7fafc; font-size: 12px; font-weight: 700; color: #4a5568; padding: 8px; }
.leave-history-table td { padding: 8px; vertical-align: middle; }
.lh-reason { font-size: 12px; color: #718096; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lh-status { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; color: #fff; }
.lh-status-approved { background: #3182ce; }
.lh-status-pending { background: #ed8936; }
.lh-status-rejected { background: #e53e3e; }
.leave-history-footer { padding: 12px 20px; border-top: 1px solid #e2e8f0; text-align: right; }

.archived-banner {
  background: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 4px;
  color: #664d03;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.6rem 0.9rem;
}
</style>
